/**
 * `access` (D265) — Access management, split out of Gate Detail's own `<section>`.
 *
 * **What moved is a panel, not a decision.** The access-list row, the Activity row, the
 * subscription row, the לוחות זמנים row and the gate-mode segmented control rendered inside one
 * manager-only `<section>` on `gate`, under the shield-glyph `accessManagementTitle` heading; they
 * render here now, in the same order, under the same title, and Gate Detail keeps one
 * `ניהול גישה ›` row where the section used to be. **D120's** enumeration is amended by D265 to
 * say so, that clause alone — not D121 (c), which `R5-responsivity.md` §4.1 named and which is
 * Recent's removal, carrying no composition list at all. Every other clause of D120, and
 * D112–D115/D118–D121, stand. The copy is the `gate`
 * namespace's, unchanged and unmoved — this screen is a second surface for P7's own strings, not a
 * namespace of its own, and the one key it adds (`latchedManageHere`) lives on Gate Detail.
 *
 * **It opens with no blocking read, and that is the point of the split's timing.** The two fields
 * it draws that a snapshot could have answered — the renewal date and whether שעון שבת is on — are
 * both in the device cache since R5 steps 1 and 2, so the first paint is complete. What Gate
 * Detail already read is handed down through `gateHandoff.ts` rather than read again, and a deep
 * link straight to this URL falls back to its own reads exactly as every screen here does.
 *
 * **Its own two requests are prefetches for the screens below it** (R5 step 6), fired on entry
 * rather than from Gate Detail: the panel is manager-only, so a Gate Detail-level prefetch would
 * spend two requests on every *resident* view for a page they can never open — and on a shared gate
 * managers are a small fraction of the roster. Neither blocks anything here, and
 * `getShabbatPreview` stays lazy: it is derived, slow, and most visits never reach it.
 *
 * **Three things it deliberately does not carry.** Gate Detail's roster read stays on Gate Detail —
 * it seeds D143's sole-manager guard for the Remove-me row, which is not on this screen. The
 * latched banner stays there too, above the button it disables, and names this page rather than
 * showing the control. And the subscription row is still a row with a badge and no destination
 * (D246); nothing here restores its `onClick`.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { GateId } from '@selogate/core'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import { useFlag } from '../../config'
import { useDeviceCache } from '../../device-cache/context'
import { AppBar, ConfirmPopover, FrameOverlay, Icon, SoonBadge } from '../../components'
import { useTranslation } from '../../i18n/I18nProvider'
import { useDismissOnBack, useNavigation } from '../../router'
import { fetchActivityPage } from '../activity/fetchActivityPage'
import { isSubscriptionExpired, renewalDaysRemaining, SUBSCRIPTION_LOW_DAYS } from '../shared/access'
import { cachedGateDetail } from '../shared/cachedGate'
import { CountBadge, DetailRow } from '../shared/DetailRow'
import { putGateHandoff, takeGateHandoff } from '../shared/gateHandoff'
import {
  getGate,
  getRoster,
  getShabbatConfig,
  latchGate,
  suspendShabbatConfig,
  type GateDetail,
  type Roster,
} from '../shared/api'

export function AccessScreen({ gateId }: { readonly gateId: GateId }): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  // R9/D329 — the panel's kill switch is a remote flag now; the compile-time constant and its
  // per-stage table are gone. Read through the subscribed hook, so a flip that lands mid-session
  // re-renders this screen rather than waiting for a relaunch.
  const shabbatPanel = useFlag('shabbat_panel')
  const { t } = useTranslation('gate')
  const { t: tCommon } = useTranslation('common')

  /**
   * **What Gate Detail already read, taken once.**
   *
   * Taken during render rather than in an effect, because the whole value of the handoff is that
   * it is available for the *first* paint. It is cached in a ref keyed to its `gateId`, which is
   * `GateDetailScreen`'s `takenToastRef` pattern and is here for the same reason: `main.tsx`
   * renders under `StrictMode`, which invokes the component body twice, and the slots are
   * read-once — a second take would answer `undefined` and blank a screen the first take had
   * filled. The ref survives the double invocation; the take does not.
   */
  const takenRef = useRef<
    { readonly gateId: GateId; readonly gate?: GateDetail; readonly roster?: Roster } | undefined
  >(undefined)
  if (takenRef.current === undefined || takenRef.current.gateId !== gateId) {
    const gate = takeGateHandoff(gateId, 'gate')
    const roster = takeGateHandoff(gateId, 'roster')
    takenRef.current = {
      gateId,
      ...(gate === undefined ? {} : { gate }),
      ...(roster === undefined ? {} : { roster }),
    }
  }
  const handed = takenRef.current

  const [gate, setGate] = useState<GateDetail | undefined>(handed.gate)
  /**
   * B1's rule, unchanged by the move: `undefined` covers both *not asked yet* and *the read
   * failed*, and both render **no badge** — never `0`, which would be a lie about the gate on a
   * failed read (D135, applied to a count).
   */
  const [membersCount, setMembersCount] = useState<number | undefined>(handed.roster?.members.length)
  const [coldFailed, setColdFailed] = useState(false)
  const [modeError, setModeError] = useState(false)
  /** P8's `501 state-reserved` (D87) — a distinct refusal, never folded into `modeError`'s copy. */
  const [modeReserved, setModeReserved] = useState(false)
  const [latchConfirm, setLatchConfirm] = useState(false)

  /**
   * D145 for D168's confirmation, registered here now that the control it guards is here.
   * Dismissible unconditionally: the write it guards has not started — cancelling issues no
   * request at all — so a back tap strands nothing.
   */
  useDismissOnBack('access:latch-confirm', latchConfirm, () => setLatchConfirm(false))

  /**
   * **The seed, and the cold deep link that is the only thing that reads.**
   *
   * One effect rather than two, so the order is decided here rather than by React: a cache hit
   * must win before any read is issued, and two effects racing would fire the request the cache
   * was about to make unnecessary. It waits for `deviceCache.origin`, which is what says the
   * snapshot has *answered* — an empty `gates` array before that is "not asked yet", and reading
   * it as "not in the cache" would issue a request on every launch.
   *
   * Gate Detail's own read is not repeated here. That read is what feeds the handoff, and where
   * there is no handoff the snapshot carries every field this screen draws (R5 steps 1 and 2). The
   * cold path — a deep link into a gate this device has never listed — is the one case with
   * nothing to paint, and it is the one case that asks.
   */
  useEffect(() => {
    if (gate !== undefined || deviceCache.origin === undefined) return
    const seeded = cachedGateDetail(deviceCache.gates, gateId)
    if (seeded !== undefined) {
      setGate(seeded)
      return
    }
    async function loadCold(): Promise<void> {
      try {
        const result = await getGate(session.request, gateId)
        if (result.ok) {
          setGate(result.data)
        } else if (result.status === 403 || result.status === 404) {
          // R1-7(b), as Gate Detail applies it: the server no longer recognizes this membership,
          // so the honest move is to leave rather than to render a mode control over it.
          navigate.resetTo({ id: 'home' })
        } else if (result.status !== 401) {
          setColdFailed(true)
        }
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error
        setColdFailed(true)
      }
    }
    void loadCold()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate, gateId, deviceCache.origin, deviceCache.gates])

  /**
   * The roster, for the access-list row's count badge alone.
   *
   * Handed down from Gate Detail on the ordinary path, so this runs on a deep link and on a manager
   * who tapped through faster than Gate Detail's own read resolved. Deliberately does **not** call
   * `markRosterSeen()` — that write is the People screen's (D89), and firing it from a row that
   * merely counts would clear the unseen badge without the manager ever having opened the roster.
   *
   * The bare `await` is caught for R1-6's reason: an offline entry must not reach an unhandled
   * rejection. A failure leaves the count `undefined`, which renders no badge.
   */
  useEffect(() => {
    if (handed.roster !== undefined) {
      // **Taken here, and passed straight on.** This screen wants a count; People wants the
      // roster. Keeping only the number was the chain's one broken link — the read path below
      // re-deposited and the handed-down path did not, so a manager who came the ordinary way
      // (Gate Detail → here → People) hit a spinner on the roster this screen was already
      // rendering the size of, while a *deep link* into this page did not. The slots are
      // read-once, so re-depositing what was taken is what "passing it on" has to look like.
      //
      // 🔸 The stated cost: a screen that leaves and comes back — D269's installation menu is the
      // one that does — takes its own deposit again and hands People a roster read at Gate Detail
      // time rather than a fresh one. It is bounded by a single visit (People *consumes* the slot,
      // so the next entry here reads for itself) and by People's own reload after every mutation.
      putGateHandoff(gateId, { roster: handed.roster })
      return
    }
    async function loadCount(): Promise<void> {
      try {
        const result = await getRoster(session.request, gateId)
        if (!result.ok) return
        setMembersCount(result.data.members.length)
        // Re-deposited rather than kept: People is the next screen down and wants the whole roster,
        // and a screen that consumed a handoff and did not pass it on is where the chain stops.
        putGateHandoff(gateId, { roster: result.data })
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error
      }
    }
    void loadCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId])

  /**
   * **R5 step 6 — the two prefetches, on entry, and the reason they are here rather than one screen
   * up.**
   *
   * Fired once per gate through a ref rather than a state flag, because `StrictMode` double-invokes
   * a mount effect and each of these is a request. Guarded on `online`: offline they would spend a
   * transport deadline each and deposit nothing.
   *
   * **A failed prefetch deposits nothing.** `fetchActivityPage`'s verdict *is* the screen's answer,
   * so a `forbidden` is deposited and honoured — but a `failed` one is a connectivity accident, and
   * handing a screen a failure it could simply have retried would make the prefetch able to make
   * things worse. Same rule for the שעון שבת read, whose only deposited refusal is the `403`/`404`
   * that sends that panel home anyway.
   */
  const prefetchedRef = useRef<GateId | undefined>(undefined)
  useEffect(() => {
    if (!deviceCache.online || prefetchedRef.current === gateId) return
    prefetchedRef.current = gateId
    async function prefetchActivity(): Promise<void> {
      const page = await fetchActivityPage(session.request, gateId, undefined)
      if (page.kind !== 'failed') putGateHandoff(gateId, { activity: page })
    }
    async function prefetchShabbat(): Promise<void> {
      const result = await getShabbatConfig(session.request, gateId)
      if (result.ok) putGateHandoff(gateId, { shabbat: { kind: 'config', config: result.data } })
      else if (result.status === 403 || result.status === 404) {
        putGateHandoff(gateId, { shabbat: { kind: 'gone' } })
      }
    }
    void prefetchActivity().catch((error: unknown) => {
      if (!(error instanceof NetworkError)) throw error
    })
    if (shabbatPanel) {
      void prefetchShabbat().catch((error: unknown) => {
        if (!(error instanceof NetworkError)) throw error
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId, deviceCache.online, shabbatPanel])

  /**
   * D174, moved here whole. **This control is the only place a gate's state is chosen, so choosing
   * anything else here has to turn "מתוזמן" off** — it suspends rather than deletes, so the
   * manager's days, times, offsets and city survive to seed the form next time, and **the
   * automation is stood down first**, because the latch has to be the last write or the exit
   * one-shot could fire between the two and take the new state back. A failed suspend aborts before
   * latching: a gate whose state changed while its schedule kept running is exactly the split the
   * four-pill control exists to prevent.
   */
  async function setMode(state: 'normal' | 'perm-closed'): Promise<void> {
    if (gate === undefined) return
    setModeError(false)
    setModeReserved(false)
    try {
      if (gate.shabbatEnabled === true) {
        const stood = await suspendShabbatConfig(session.request, gate.gateId)
        if (!stood.ok) {
          setModeError(true)
          return
        }
        setGate({ ...gate, shabbatEnabled: false })
      }
      const result = await latchGate(session.request, gate.gateId, state)
      if (result.ok) {
        setGate({ ...gate, shabbatEnabled: false, operatingState: result.data.operatingState })
        // Home and Gate Detail both read `operatingState` off the device cache, not off this
        // screen's state — without this, a tile set to סגור קבוע keeps showing its pre-latch state
        // until the next unrelated refresh, and so does the banner one screen up.
        void deviceCache.refresh()
      } else if (result.status === 501 && result.code === 'state-reserved') {
        setModeReserved(true)
      } else {
        setModeError(true)
      }
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setModeError(true)
    }
  }

  if (gate === undefined) {
    return (
      <>
        <AppBar title={t('accessManagementTitle')} onBack={() => navigate.back()} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
          {coldFailed ? t('notFound') : tCommon('loading')}
        </div>
      </>
    )
  }

  const expired = gate.subscription !== undefined && isSubscriptionExpired(gate.subscription.renewsAt)
  const subscriptionDays =
    gate.subscription === undefined ? null : renewalDaysRemaining(gate.subscription.renewsAt)
  const subscriptionBadgeVariant: 'green' | 'amber' | 'red' | undefined =
    subscriptionDays === null
      ? undefined
      : subscriptionDays < SUBSCRIPTION_LOW_DAYS
        ? 'red'
        : subscriptionDays < SUBSCRIPTION_LOW_DAYS * 2
          ? 'amber'
          : 'green'
  /**
   * D103/D-A — whether the gate's own Shabbat automation is currently enabled, and **it takes
   * priority over `operatingState` for the selection, exclusively**: a single-select control cannot
   * show two segments selected at once, and a gate can be `operatingState: 'normal'` with the
   * automation already armed for the coming Shabbat.
   *
   * **Since R5 step 2 this is answered on the first frame of a cache-seeded render.** It used to be
   * absent there, which made `scheduleActive` `false` and lit "רגיל" on a gate whose automation was
   * on — and a tap inside that window skipped `suspendShabbatConfig` entirely.
   */
  const scheduleActive = gate.shabbatEnabled === true
  const normalSelected =
    !scheduleActive && (gate.operatingState === 'normal' || gate.operatingState === undefined)
  const permClosedSelected = !scheduleActive && gate.operatingState === 'perm-closed'

  return (
    <>
      <AppBar title={t('accessManagementTitle')} onBack={() => navigate.back()} />

      <section className="gate-panel">
        <h2 className="gate-panel-title">
          <Icon name="shield" size={16} /> {t('accessManagementTitle')}
        </h2>
        {expired ? <div className="gate-card-hint">{t('expiredHint')}</div> : null}
        <DetailRow
          label={t('accessListRow')}
          value={
            <>
              {gate.unseenExpired !== undefined && gate.unseenExpired > 0 ? String(gate.unseenExpired) : ''}
              {/* B1 — neutral, always: a count, not a status. Beside the unseen-expired number
                  above, never instead of it (D89's badge stays a shipped surface). */}
              {membersCount !== undefined ? (
                <CountBadge>
                  {t(membersCount === 1 ? 'membersCountOne' : 'membersCountOther', { count: membersCount })}
                </CountBadge>
              ) : null}
            </>
          }
          onClick={() => navigate.navigate({ id: 'people', gateId })}
        />
        {/* P9 — Activity is manager-only, reached only from here (D29, EC-17): there is no
            standalone Activity tab. */}
        <DetailRow
          label={t('activityRow')}
          value=""
          onClick={() => navigate.navigate({ id: 'activity', gateId })}
        />
        {/* D246 — the row keeps its number and loses its destination. With no `onClick`,
            `DetailRow` renders a `<div>` with no chevron instead of a `<button>`, which is the
            whole of that change: §13's *Every manager of a gate sees the same renewal countdown*
            (D52) is satisfied by this badge and never was by the screen it used to open. */}
        <DetailRow
          label={t('subscriptionRow')}
          value={
            subscriptionDays === null ? (
              ''
            ) : (
              <CountBadge
                {...(subscriptionBadgeVariant === undefined ? {} : { variant: subscriptionBadgeVariant })}
              >
                {t('daysRemainingBadge', { count: subscriptionDays })}
              </CountBadge>
            )
          }
        />
        {shabbatPanel ? (
          <DetailRow
            label={t('schedulesRow')}
            value={gate.shabbatEnabled === true ? tCommon('on') : tCommon('off')}
            onClick={() => navigate.navigate({ id: 'shabbat', gateId })}
          />
        ) : null}
        {/*
          D267 — the second door onto D176's update branch, and the whole of what this decision
          adds to this screen. The two editors behind it are unchanged: `InstallScreen` renders
          them in `mode: 'update'`, seeded straight to `updateMenu`, so the shared address
          validation stays in one place (a duplicated naming screen is one refactor away from a
          path that can *erase* an address, which is D176 note 2).

          The handoff deposit is `GateDetailScreen`'s own pattern, at the tap rather than at the
          read: `gate` is whatever this screen is currently rendering, which is what the menu
          would have painted from anyway. It is what lets the menu open with its name line filled
          and no `GET /gates/{gateId}` — a cold deep link straight to the URL takes the miss path
          and reads for itself, exactly as D176 always did.
        */}
        <DetailRow
          label={t('installationRow')}
          value=""
          onClick={() => {
            putGateHandoff(gateId, { gate })
            navigate.navigate({ id: 'installEdit', gateId })
          }}
        />
        <div className="gate-mode-block">
          <div className="gate-mode-label">{t('gateModeLabel')}</div>
          {/*
            D-A/D87 — a single-select segmented control. "מתוזמן" stands for the gate's own Shabbat
            automation (`scheduleActive` above) rather than a mode this screen sets directly — it
            always opens the same schedule screen the Schedules row does, and is selected exactly
            when the automation is on. **Its Soon badge is tied to the shabbat_panel flag alone,
            never to whether this gate has a schedule configured** — an unconfigured gate is an
            ordinary, actionable state, not an unavailable one. "פתוח קבוע" is always disabled with
            a Soon badge: D87 reserves and refuses that operating state in Stage 1, and a target the
            command path refuses must never be configurable — only visible as a preview.
          */}
          <div className="gate-mode-segmented" role="group" aria-label={t('gateModeLabel')}>
            <button
              className={`gate-mode-segment${normalSelected ? ' selected' : ''}`}
              type="button"
              data-state="normal"
              aria-pressed={normalSelected}
              disabled={deviceCache.readOnly || expired}
              onClick={() => void setMode('normal')}
            >
              {t('modeNormal')}
            </button>
            <button
              className={`gate-mode-segment${scheduleActive ? ' selected' : ''}`}
              type="button"
              data-state="scheduled"
              aria-pressed={scheduleActive}
              disabled={deviceCache.readOnly || expired || !shabbatPanel}
              onClick={() => navigate.navigate({ id: 'shabbat', gateId })}
            >
              {t('modeScheduled')}
              {shabbatPanel ? null : <SoonBadge />}
            </button>
            <button
              className={`gate-mode-segment${permClosedSelected ? ' selected' : ''}`}
              type="button"
              data-state="perm-closed"
              aria-pressed={permClosedSelected}
              disabled={deviceCache.readOnly || expired}
              // D168: this tap opens the confirmation; only its confirm writes. The `normal`
              // segment above stays a direct write — leaving the state restores access and asks
              // nothing.
              onClick={() => {
                setModeError(false)
                setModeReserved(false)
                setLatchConfirm(true)
              }}
            >
              {t('modePermClosed')}
            </button>
            <button
              className="gate-mode-segment"
              type="button"
              data-state="perm-open"
              aria-pressed={false}
              disabled
            >
              {t('modePermOpen')}
              <SoonBadge />
            </button>
          </div>
          {/* F9/R1 — D82's physical-remote sentence, per §13 "Gate Detail follows D120's hierarchy
              in Hebrew and English", which requires it beside Permanent close. It travels with the
              control rather than staying behind on a screen that no longer has one. A mode refusal
              still takes precedence over it: a manager acting on a refused tap needs to see why it
              failed, not the standing residual risk. */}
          {modeReserved ? (
            <p>{t('modeReserved')}</p>
          ) : modeError ? (
            <p>{t('modeError')}</p>
          ) : (
            <p className="gate-remote-warning">{t('remoteWarning')}</p>
          )}
        </div>
      </section>

      {latchConfirm ? (
        <FrameOverlay>
          <ConfirmPopover
            title={t('latchConfirmTitle')}
            body={t('latchConfirmBody')}
            cancelLabel={tCommon('cancel')}
            confirmLabel={t('latchConfirmAction')}
            onCancel={() => setLatchConfirm(false)}
            onConfirm={() => {
              setLatchConfirm(false)
              void setMode('perm-closed')
            }}
          />
        </FrameOverlay>
      ) : null}
    </>
  )
}
